import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Grid,
  Paper,
  Chip,
  Tooltip,
  Container,
  Fade
} from '@mui/material';
import { investmentAPI } from '../../api/investmentAPI';
import { HeatmapsAllData, HeatmapsAllStockItem } from '../../types/investment';
import { formatPercent, formatNumber } from '../../utils/formatters';
import { green, red, grey } from '@mui/material/colors';
import { useLoadingStatus } from '../../contexts/LoadingStatusContext';

// --- Category Performance Heatmap Component ---
interface CategoryPerformanceData {
  categoryName: string;
  stocks: HeatmapsAllStockItem[];
}

// Updated helper function for color scaling using logarithmic scale
const getColorForValue = (value: number | null): string => {
  if (value === null || value === undefined || value === 0) return grey[300];

  const sign = value > 0 ? 1 : -1;
  const absValue = Math.abs(value);

  // Logarithmic scaling (adjust multiplier as needed)
  const logIntensity = Math.min(Math.log10(absValue + 1) / 2, 1); // +1 to avoid log(0), adjust scale as needed
  const shadeNumber = Math.floor(100 + logIntensity * 600);
  const calculatedShade = Math.max(100, Math.min(700, Math.round(shadeNumber / 100) * 100));
  const finalShade = calculatedShade as 100 | 200 | 300 | 400 | 500 | 600 | 700;

  return sign > 0 ? green[finalShade] : red[finalShade];
};

const CategoryPerformanceHeatmap: React.FC<{
  data: CategoryPerformanceData[];
  onTickerSelect: (ticker: string) => void;
  categoryName: string;
}> = ({ data, onTickerSelect, categoryName }) => {
  if (!data || data.length === 0) {
    return (
      <Typography variant="body2" sx={{ p: 2, fontStyle: 'italic' }}>
        No data available for this category.
      </Typography>
    );
  }

  return (
    <Fade in={true}>
      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1, mb: 2 }}>
          {categoryName}
        </Typography>
        <Grid container spacing={3}>
          {data.map((category) => {
            // Sort stocks: top performers first (higher overall_gain), then bottom performers (lower overall_gain)
            const sortedStocks = [...category.stocks].sort((a, b) => b.overall_gain - a.overall_gain);
            
            return (
              <Grid item xs={12} sm={6} md={4} key={category.categoryName}>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {category.categoryName}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {sortedStocks.map((stock) => (
                      <Tooltip 
                        key={stock.ticker} 
                        title={stock.new 
                          ? "New entry in this list. Click for details." 
                          : "Click for detailed analysis."
                        }
                      >
                        <Chip
                          label={
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column',
                              position: 'relative',
                              pr: stock.new ? 1 : 0, // Add padding on right if new
                              width: '100%', // Ensure content takes full width
                              alignItems: 'center' // Center content horizontally
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography component="span" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                                  {stock.ticker}
                                </Typography>
                                {stock.new && (
                                  <Box 
                                    sx={{ 
                                      width: 6, 
                                      height: 6, 
                                      borderRadius: '50%', 
                                      bgcolor: 'primary.main',
                                      position: 'absolute',
                                      top: 1,
                                      right: -2,
                                      boxShadow: '0 0 0 1px white'
                                    }} 
                                  />
                                )}
                              </Box>
                              <Typography component="span" sx={{ fontSize: '0.65rem', opacity: 0.9 }}>
                                ${formatNumber(stock.close)} ({formatPercent(stock.gain_last_1day/100)})
                              </Typography>
                            </Box>
                          }
                          onClick={() => onTickerSelect(stock.ticker)}
                          sx={{
                            bgcolor: getColorForValue(stock.overall_gain),
                            color: 'white',
                            fontWeight: 'medium',
                            cursor: 'pointer',
                            height: 'auto',
                            width: '85px', // Reduced width for smaller chips
                            py: 0.3, // Less vertical padding
                            px: 0.5, // Less horizontal padding
                            '&:hover': { opacity: 0.8, transform: 'translateY(-1px)', transition: 'transform 0.2s' },
                            '& .MuiChip-label': {
                              px: 0.5, // Tighter padding inside chip label
                            },
                            transition: 'transform 0.2s'
                          }}
                          size="small" // Use small size for chips
                        />
                      </Tooltip>
                    ))}
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    </Fade>
  );
};

// --- Main StockDiscovery Component ---
interface StockDiscoveryProps {
  onTickerSelect: (ticker: string) => void;
}

// Helper function to get pivot value
const getPivotValue = (stock: HeatmapsAllStockItem, key: string): number | undefined => {
  switch (key) {
    case 'Historical Performance':
      return stock.gain_last_6months ?? stock.gain_last_1month ?? stock.gain_last_2weeks;
    case 'Projected Performance':
      return stock.LongTerm_expected_return ?? stock.MidTerm_expected_return ?? stock.ShortTerm_expected_return;
    default:
      return undefined;
  }
};

const StockDiscovery: React.FC<StockDiscoveryProps> = ({ onTickerSelect }) => {
  const [data, setData] = useState<HeatmapsAllData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedPivot, setSelectedPivot] = useState<string>('');
  const { addStatus, removeStatus } = useLoadingStatus();
  const api = useMemo(() => investmentAPI(addStatus, removeStatus), []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await api.getHeatmapsAll();
        setData(result);
        // Set initial category and pivot if data is available
        const categories = Object.keys(result);
        if (categories.length > 0) {
          const firstCategory = categories[0];
          setSelectedCategory(firstCategory);
          // Ensure the first pivot exists before setting it
          const pivots = Object.keys(result[firstCategory] || {});
          if (pivots.length > 0) {
            setSelectedPivot(pivots[0]);
          }
        }
      } catch (err: any) {
        console.error("Error fetching all heatmap data:", err);
        setError(err.message || 'Failed to load stock discovery data.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api]);

  const handleCategoryChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedCategory(newValue);
    // Reset pivot to the first available for the new category
    if (data && data[newValue]) {
      const pivots = Object.keys(data[newValue]);
      if (pivots.length > 0) {
        setSelectedPivot(pivots[0]);
      } else {
        setSelectedPivot('');
      }
    }
  };

  const handlePivotChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedPivot(newValue);
  };

  // --- Data Aggregation Logic for Heatmap ---
  const aggregatedPerformanceData = useMemo(() => {
    if (!data || !selectedPivot || !selectedCategory) return [];

    const pivotData = data[selectedCategory]?.[selectedPivot];
    if (!pivotData) return [];

    const categoriesSet = new Set([
      ...Object.keys(pivotData.top || {}),
      ...Object.keys(pivotData.bottom || {})
    ]);

    const results: CategoryPerformanceData[] = [];

    categoriesSet.forEach((categoryName) => {
      const topStocks = pivotData.top?.[categoryName] || [];
      const bottomStocks = pivotData.bottom?.[categoryName] || [];

      // Merge top and bottom stocks into a single array
      const stocks = [...topStocks, ...bottomStocks];

      results.push({
        categoryName,
        stocks, // Now matches the updated interface
      });
    });

    results.sort((a, b) => a.categoryName.localeCompare(b.categoryName));

    return results;
  }, [data, selectedPivot, selectedCategory]);
  // --- End Data Aggregation Logic ---

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      </Container>
    );
  }

  if (!data || Object.keys(data).length === 0) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="info">No stock discovery data available.</Alert>
      </Container>
    );
  }

  const categories = Object.keys(data);
  // Ensure selectedCategory exists in data before accessing its keys
  const pivots = selectedCategory && data[selectedCategory] ? Object.keys(data[selectedCategory]) : [];
  // Ensure selectedPivot exists for the selectedCategory before accessing data
  const currentPivotData = selectedCategory && selectedPivot && data[selectedCategory]?.[selectedPivot];

  // Format the pivot name for display
  const formatPivotName = (name: string) => {
    return name === 'Historical Performance' ? 'Historical' : name === 'Projected Performance' ? 'Projected' : name;
  };

  return (
    <Box>
      {/* Streamlined header area with less vertical space */}
      <Box sx={{ mb: 2 }}> {/* Reduced margin bottom */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}> {/* Flex container for title + info */}
          <Typography variant="h5" component="h1">Stock Discovery</Typography> {/* Reduced from h4 to h5 */}
          
          <Box sx={{ 
            display: 'inline-flex', 
            alignItems: 'center',
            backgroundColor: 'background.paper', 
            px: 1, 
            py: 0.5, 
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                mr: 0.5
              }}
            />
            <Typography variant="caption" color="text.secondary">
              indicates new entries
            </Typography>
          </Box>
        </Box>

        {/* Combined category & pivot navigation */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
          {/* Category Tabs - More compact */}
          <Paper elevation={1} sx={{ 
            borderRadius: 2, 
            overflow: 'hidden',
            flexGrow: 1,
            flexBasis: { xs: '100%', sm: '50%' }
          }}>
            <Tabs 
              value={selectedCategory} 
              onChange={handleCategoryChange}
              variant="fullWidth"
              sx={{ 
                minHeight: '36px', // Reduced height
                '& .MuiTab-root': {
                  textTransform: 'none',
                  py: 1, // Reduced padding
                  fontWeight: 500,
                  minHeight: '36px' // Reduced height
                }
              }}
            >
              {categories.map((cat) => (
                <Tab key={cat} label={cat} value={cat} />
              ))}
            </Tabs>
          </Paper>

          {/* Pivot Tabs - Styled with pills, position alongside category tabs */}
          {selectedCategory && pivots.length > 0 && (
            <Tabs 
              value={selectedPivot} 
              onChange={handlePivotChange} 
              variant="scrollable" 
              scrollButtons="auto"
              sx={{
                flexGrow: 0,
                flexBasis: { xs: '100%', sm: '50%' },
                minHeight: '36px', // Reduced height
                borderRadius: 2,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  borderRadius: 5,
                  minHeight: '28px', // Reduced height
                  px: 2,
                  py: 1, // Reduced padding
                  mx: 0.5,
                  fontSize: '0.85rem', // Smaller font
                  fontWeight: 500,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white'
                  }
                },
                '& .MuiTabs-indicator': {
                  display: 'none'
                }
              }}
            >
              {pivots.map((piv) => (
                <Tab key={piv} label={formatPivotName(piv)} value={piv} />
              ))}
            </Tabs>
          )}
        </Box>
      </Box>

      {/* Performance Heatmap - Render directly without category name heading */}
      {selectedPivot && (
        <Fade in={true}>
          <Box sx={{ pt: 1 }}>{/* Small padding at top, removed Paper wrapper */}
            {aggregatedPerformanceData.length > 0 ? (
              <Grid container spacing={2}>
                {aggregatedPerformanceData.map((category) => {
                  // Sort stocks: top performers first (higher overall_gain), then bottom performers (lower overall_gain)
                  const sortedStocks = [...category.stocks].sort((a, b) => b.overall_gain - a.overall_gain);

                  return (
                    <Grid item xs={12} sm={6} md={4} key={category.categoryName}>
                      <Paper elevation={1} sx={{ p: 1.5, borderRadius: 2 }}>{/* Added Paper per category with less padding */}
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                          {category.categoryName}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {sortedStocks.map((stock) => (
                            <Tooltip
                              key={stock.ticker}
                              title={stock.new
                                ? "New entry in this list. Click for details."
                                : "Click for detailed analysis."
                              }
                            >
                              <Chip
                                label={
                                  <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    position: 'relative',
                                    pr: stock.new ? 1 : 0, // Add padding on right if new
                                    width: '100%', // Ensure content takes full width
                                    alignItems: 'center' // Center content horizontally
                                  }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Typography component="span" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                                        {stock.ticker}
                                      </Typography>
                                      {stock.new && (
                                        <Box
                                          sx={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            bgcolor: 'primary.main',
                                            position: 'absolute',
                                            top: 1,
                                            right: -2,
                                            boxShadow: '0 0 0 1px white'
                                          }}
                                        />
                                      )}
                                    </Box>
                                    <Typography component="span" sx={{ fontSize: '0.65rem', opacity: 0.9 }}>
                                      ${formatNumber(stock.close)} ({formatPercent(stock.gain_last_1day / 100)})
                                    </Typography>
                                  </Box>
                                }
                                onClick={() => onTickerSelect(stock.ticker)}
                                sx={{
                                  bgcolor: getColorForValue(stock.overall_gain),
                                  color: 'white',
                                  fontWeight: 'medium',
                                  cursor: 'pointer',
                                  height: 'auto',
                                  width: '85px', // Reduced width for smaller chips
                                  py: 0.3, // Less vertical padding
                                  px: 0.5, // Less horizontal padding
                                  '&:hover': { opacity: 0.8, transform: 'translateY(-1px)', transition: 'transform 0.2s' },
                                  '& .MuiChip-label': {
                                    px: 0.5, // Tighter padding inside chip label
                                  },
                                  transition: 'transform 0.2s'
                                }}
                                size="small" // Use small size for chips
                              />
                            </Tooltip>
                          ))}
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Typography variant="body2" sx={{ p: 2, fontStyle: 'italic' }}>
                No data available for this category.
              </Typography>
            )}
          </Box>
        </Fade>
      )}

      {/* Show message if no data exists for the selection */}
      {!currentPivotData && selectedCategory && selectedPivot && (
        <Alert severity="warning">No data available for the selected category and pivot combination.</Alert>
      )}
    </Box>
  );
};

export default StockDiscovery;
